import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import ArticleCard from "../../components/shared/ArticleCard";
import AskAuriAboutThis from "../../components/shared/AskAuriAboutThis";
import { Clock, CalendarDays } from "lucide-react";

/**
 * Public Article Detail — CMS-driven.
 * Fetches from `/api/public/articles/:slug` (which already enforces
 * is_published=true AND is_archived!=true). Related articles are pulled
 * from the same public list endpoint and filtered client-side.
 *
 * Preserves: routing, slug URLs, SEO meta (title/description via document
 * head), page layout, first-letter drop-cap treatment, and the AURI
 * "Ask About This Article" nudge.
 */
export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setArticle(null);
    api.get(`/public/articles/${slug}`)
      .then(r => { if (!cancelled) setArticle(r.data); })
      .catch(err => {
        if (!cancelled) setNotFound(err?.response?.status === 404);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/articles")
      .then(r => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setRelated(list.filter(a => a.slug !== slug).slice(0, 3));
      })
      .catch(() => setRelated([]));
    return () => { cancelled = true; };
  }, [slug]);

  // SEO — set the document title from the article's seo_title (or title)
  useEffect(() => {
    if (!article) return;
    const prev = document.title;
    document.title = article.seo_title || `${article.title} — Altura Global`;
    return () => { document.title = prev; };
  }, [article]);

  if (loading) return <div className="container-editorial py-32 text-sm text-altura-charcoal/50 italic">Loading essay…</div>;
  if (notFound || !article) {
    return (
      <div className="container-editorial py-32">
        <div className="text-sm uppercase tracking-widest text-altura-charcoal/50">Not found</div>
        <div className="mt-3 font-editorial text-3xl text-altura-charcoal">This essay isn't published yet.</div>
        <Link to="/learning" className="mt-6 inline-block text-sm text-altura-gold-dark hover:text-altura-espresso link-underline">← Back to the Learning Center</Link>
      </div>
    );
  }

  // Body is stored as a single markdown-ish string separated by blank lines.
  const paragraphs = (article.body || "").split(/\n{2,}/).filter(Boolean);

  return (
    <div data-testid={`page-article-${slug}`}>
      <section className="bg-altura-ivory py-16">
        <div className="container-editorial max-w-3xl">
          <Link to="/learning" className="text-xs uppercase tracking-widest text-altura-espresso/60 hover:text-altura-espresso">← Learning Center</Link>
          <Eyebrow className="mt-6">{article.category}{article.market ? ` · ${article.market}` : ""}</Eyebrow>
          <EditorialHeading size="lg" className="mt-4">{article.title}</EditorialHeading>
          <div className="mt-6 flex items-center gap-4 text-[12px] text-altura-charcoal/60 font-mono">
            {article.published_at ? (
              <>
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{new Date(article.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                <span>·</span>
              </>
            ) : null}
            {article.reading_time && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{article.reading_time}</span>}
          </div>
          <GoldDivider className="mt-8" />
        </div>
      </section>

      {article.featured_image && (
        <section className="bg-altura-ivory">
          <div className="container-editorial max-w-5xl">
            <div className="aspect-[16/9] overflow-hidden rounded-lg">
              <img src={article.featured_image} alt={article.title} className="h-full w-full object-cover" />
            </div>
          </div>
        </section>
      )}

      <article className="bg-altura-ivory py-16">
        <div className="container-editorial max-w-3xl">
          <div className="prose prose-lg max-w-none font-sans text-altura-charcoal/85 leading-[1.9]">
            {paragraphs.map((p, i) => (
              <p key={i} className={`${i === 0 ? "first-letter:font-editorial first-letter:text-6xl first-letter:font-medium first-letter:text-altura-espresso first-letter:mr-2 first-letter:float-left first-letter:leading-[0.9]" : ""} mb-6`}>{p}</p>
            ))}
          </div>
          <div className="mt-16 border-t border-altura-espresso/10 pt-8 text-xs text-altura-charcoal/55">
            Altura Global does not provide legal, tax, immigration, or financial advice. Editorial content is for education only.
          </div>
          <div className="mt-10">
            <AskAuriAboutThis kind="article" title={article.title} slug={article.slug} />
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-altura-beige/40 py-24">
          <div className="container-editorial">
            <div className="mb-10 font-editorial text-3xl text-altura-charcoal">More essays</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a) => <ArticleCard key={a.slug} article={{ ...a, image: a.featured_image, date: a.published_at }} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
